// Keep credentials and personal data out of Sentry (mayainsights#1804).
// Page URLs carry secrets: `mcp_auth_token` on the sign-in / organization-select flow,
// OAuth `?code=` / `state=` on provider redirects, invitation ids in the path.
// @sentry/react records them in `request.url`, navigation breadcrumbs
// (`data.from` / `data.to`) and xhr/fetch breadcrumbs (`data.url`), and console
// breadcrumbs carry whatever was logged.
//
// Same URL rule as the backend (mayainsights/sentry_scrub.py): keep scheme + host and the
// first path segment when it is a plain word, replace every later segment with `*`, and
// replace the query with `?[Filtered]`.
//
// Every regex is linear: no lookbehind (unsupported by older Safari, and a parse error
// would take the whole bundle down), and every match start is anchored at a token
// boundary by a consumed prefix group. Strings are cut to MAX_TEXT before any pattern runs.

const FILTERED = '[Filtered]'
const MAX_TEXT = 1024
const MAX_DEPTH = 10

const SENSITIVE_WORD = [
  'token', 'tokens', 'secret', 'secrets', 'password', 'passwords', 'passwd', 'pwd', 'auth',
  'authorization', 'cookie', 'cookies', 'signature', 'csrf', 'xsrf', 'key', 'keys',
  'verifier', 'credential', 'credentials', 'bearer', 'jwt', 'dsn', 'passphrase', 'otp',
]
const SENSITIVE_EXACT = ['code', 'state', 'grant', 'session', 'sessionid', 'apikey', 'setcookie']
const SENSITIVE_SUFFIX = [
  'token', 'secret', 'password', 'passwd', 'sessionid', 'apikey', 'authcode',
  'authorizationcode', 'codeverifier', 'devicecode', 'privatekey', 'secretkey',
]
const NAME_WORDS = /[A-Z]+(?![a-z])|[A-Z]?[a-z]+|\d+/g

export const isSensitiveName = (name: string): boolean => {
  const n = name.slice(0, 128)
  const joined = n.toLowerCase().replace(/[^a-z0-9]/g, '')
  if (SENSITIVE_EXACT.indexOf(joined) !== -1) return true
  if (SENSITIVE_SUFFIX.some((s) => joined.length >= s.length && joined.slice(-s.length) === s)) {
    return true
  }
  return (n.match(NAME_WORDS) || []).some((w) => SENSITIVE_WORD.indexOf(w.toLowerCase()) !== -1)
}

const SAFE_WORD = /^[A-Za-z_-]{1,40}$/
const TEMPLATE = /^(\{[^}]*\}|<[^>]*>|:[A-Za-z_]+)$/

const shapePath = (path: string): string => {
  const cut = path.search(/[?#]/)
  const bare = cut === -1 ? path : path.slice(0, cut)
  let first = true
  const shaped = bare
    .split('/')
    .map((seg) => {
      if (!seg) return seg
      const keep = TEMPLATE.test(seg) || (first && SAFE_WORD.test(seg))
      first = false
      return keep ? seg : '*'
    })
    .join('/')
  return cut === -1 ? shaped : `${shaped}?${FILTERED}`
}

const URL_PARTS = /^([A-Za-z][A-Za-z0-9+.-]{0,63}:\/\/)([^/?#\s]*)([\s\S]*)$/

// A URL or path: scheme://host/first/*/*?[Filtered]. Anything else goes through scrubText.
export const shapeUrl = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  const v = value.slice(0, MAX_TEXT)
  const m = URL_PARTS.exec(v)
  if (m) {
    const host = m[2].slice(m[2].lastIndexOf('@') + 1)
    return m[1] + host + shapePath(m[3])
  }
  if (v.charAt(0) === '/' || (v.indexOf('?') !== -1 && v.indexOf(' ') === -1)) return shapePath(v)
  return scrubText(v)
}

// Each pattern consumes a one-character boundary prefix (or matches at ^) instead of a
// lookbehind; the prefix is written back unchanged.
const SCHEME_URL = /(^|[^A-Za-z0-9+.-])([A-Za-z][A-Za-z0-9+.-]{0,63}:\/\/[^\s"'<>]*)/g
const BARE_PATH = /(^|[^\w.:/~@%-])(\/[^\s"'<>]+)/g
const JWT = /(^|[^\w.-])eyJ[\w-]{2,}\.[\w-]{2,}\.[\w-]*/g
// The name match stops at the separator, so whitespace after it is left for the next
// name's boundary prefix (`failed: access_token=x`); the value match skips it.
const PAIR_NAME = /(^|[^\w.-])([\w.-]{1,64})(["']?\s{0,3}[:=])/g
const PAIR_VALUE = /^(\s{0,3}["']?(?:bearer\s{1,3})?)([^"'&\s,;}\])]+)/i
const BEARER = /(^|[^\w-])(bearer\s{1,3})([^"'\s,;}\])]+)/gi
const EMAIL = /(^|[^\w.+-])[\w.+-]{1,64}@[\w-]{1,63}(?:\.[\w-]{1,63})+/g
const TOKEN_RUN = /(^|[^\w-])([\w-]{20,})(?![\w-])/g
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

const redactPairs = (text: string): string => {
  let out = ''
  let last = 0
  PAIR_NAME.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = PAIR_NAME.exec(text)) !== null) {
    if (m.index < last || !isSensitiveName(m[2])) continue
    const start = m.index + m[0].length
    const v = PAIR_VALUE.exec(text.slice(start)) // text is already capped at MAX_TEXT
    if (!v || v[2].indexOf('[Filtered') === 0) continue
    out += text.slice(last, start + v[1].length) + FILTERED
    last = start + v[0].length
    PAIR_NAME.lastIndex = last
  }
  return out + text.slice(last)
}

const tokenOrKeep = (s: string): string => {
  if (UUID.test(s)) return s // Power BI workspace / dataset ids
  if (!/\d/.test(s) || !/[A-Za-z]/.test(s)) return s
  // snake_case / kebab identifiers are kept; random tokens mix letters and digits.
  if (s.split(/[_-]/).every((seg) => !seg || /^[A-Za-z]+$/.test(seg) || /^\d+$/.test(seg))) return s
  return FILTERED
}

export const scrubText = (text: unknown): unknown => {
  if (typeof text !== 'string') return text
  let t = text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT)}...[truncated]` : text
  t = t.replace(SCHEME_URL, (_m, pre: string, url: string) => pre + (shapeUrl(url) as string))
  t = t.replace(BARE_PATH, (_m, pre: string, path: string) =>
    pre + (path.split('/').length < 3 && !/[?#]/.test(path) ? path : shapePath(path)))
  t = t.replace(JWT, (_m, pre: string) => pre + FILTERED)
  t = redactPairs(t)
  t = t.replace(BEARER, (m, pre: string, b: string, v: string) =>
    v.indexOf('[Filtered') === 0 ? m : pre + b + FILTERED)
  t = t.replace(EMAIL, (_m, pre: string) => pre + FILTERED)
  return t.replace(TOKEN_RUN, (_m, pre: string, run: string) => pre + tokenOrKeep(run))
}

const URL_KEYS = ['url', 'from', 'to']

// Scope data (extra / contexts / tags / breadcrumb data): sensitive key names are
// filtered, URL-valued keys are shaped, every other string is text-scrubbed.
export const scrubData = (value: any, depth = 0): any => {
  if (depth > MAX_DEPTH) return FILTERED
  if (typeof value === 'string') return scrubText(value)
  if (Array.isArray(value)) return value.map((v) => scrubData(v, depth + 1))
  if (value && typeof value === 'object') {
    const out: any = {}
    Object.keys(value).forEach((key) => {
      const item = value[key]
      if (isSensitiveName(key) && typeof item !== 'number' && typeof item !== 'boolean' && item != null) {
        out[key] = FILTERED
      } else if (URL_KEYS.indexOf(key) !== -1 && typeof item === 'string') {
        out[key] = shapeUrl(item)
      } else {
        out[key] = scrubData(item, depth + 1)
      }
    })
    return out
  }
  return value
}

export const scrubBreadcrumb = (breadcrumb: any): any | null => {
  if (!breadcrumb) return breadcrumb
  // Console breadcrumbs are free text (often whole API responses); never send them.
  if (breadcrumb.category === 'console') return null
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    breadcrumb = { ...breadcrumb, data: scrubData(breadcrumb.data) }
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb = { ...breadcrumb, message: scrubText(breadcrumb.message) }
  }
  return breadcrumb
}

export const scrubEvent = (event: any): any | null => {
  try {
    if (event.request) {
      const request = { ...event.request }
      request.url = shapeUrl(request.url)
      delete request.query_string
      delete request.cookies
      delete request.data
      if (request.headers) {
        const headers = { ...request.headers }
        delete headers.Referer
        delete headers.referer
        request.headers = scrubData(headers)
      }
      event = { ...event, request }
    }
    if (event.breadcrumbs) {
      event = {
        ...event,
        breadcrumbs: event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean),
      }
    }
    if (event.exception && event.exception.values) {
      event.exception.values.forEach((value: any) => {
        value.value = scrubText(value.value)
      })
    }
    if (typeof event.message === 'string') event.message = scrubText(event.message)
    ;['extra', 'contexts', 'tags'].forEach((k) => {
      if (event[k]) event[k] = scrubData(event[k])
    })
    if (event.user) event.user = event.user.id !== undefined ? { id: event.user.id } : undefined
    return event
  } catch (e) {
    return null
  }
}
